import { supabase } from '../lib/supabase';

export interface MeasurementPhoto {
  id: string;
  measurement_id: string;
  file_name: string;
  file_path: string;
  file_size?: number;
  mime_type?: string;
  uploaded_by?: string;
  created_at: string;
  updated_at: string;
}

export interface UploadMeasurementPhotoParams {
  measurementId: string;
  file: File;
}

export interface UploadMeasurementPhotoResult {
  success: boolean;
  photo?: MeasurementPhoto;
  error?: string;
}

/**
 * Upload a photo for a measurement
 */
export async function uploadMeasurementPhoto({
  measurementId,
  file
}: UploadMeasurementPhotoParams): Promise<UploadMeasurementPhotoResult> {
  try {
    // Generate unique file name
    const fileExt = file.name.split('.').pop();
    const fileName = `${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`;
    const filePath = `measurement-photos/${measurementId}/${fileName}`;

    // Upload file to Supabase storage
    const { data: uploadData, error: uploadError } = await supabase.storage
      .from('user-uploads')
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: false
      });

    if (uploadError) {
      console.error('Storage upload error:', uploadError);
      return { success: false, error: `Upload failed: ${uploadError.message}` };
    }

    // Get current user for uploaded_by
    const { data: { user } } = await supabase.auth.getUser();

    // Save photo record to database
    const { data: photoData, error: dbError } = await supabase
      .from('measurement_photos')
      .insert({
        measurement_id: measurementId,
        file_name: file.name,
        file_path: filePath,
        file_size: file.size,
        mime_type: file.type,
        uploaded_by: user?.id || null
      })
      .select()
      .single();

    if (dbError) {
      console.error('Database insert error:', dbError);
      
      // Clean up uploaded file if database insert fails
      await supabase.storage
        .from('user-uploads')
        .remove([filePath]);
      
      return { success: false, error: `Database error: ${dbError.message}` };
    }

    return { success: true, photo: photoData };
  } catch (error) {
    console.error('Unexpected error uploading measurement photo:', error);
    return { success: false, error: 'Unexpected error occurred' };
  }
}

/**
 * Get all photos for a measurement
 */
export async function getMeasurementPhotos(measurementId: string): Promise<MeasurementPhoto[]> {
  try {
    const { data, error } = await supabase
      .from('measurement_photos')
      .select('*')
      .eq('measurement_id', measurementId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching measurement photos:', error);
      return [];
    }

    return data || [];
  } catch (error) {
    console.error('Unexpected error fetching measurement photos:', error);
    return [];
  }
}

/**
 * Delete a measurement photo
 */
export async function deleteMeasurementPhoto(photoId: string): Promise<boolean> {
  try {
    // First get the photo record to get the file path
    const { data: photo, error: fetchError } = await supabase
      .from('measurement_photos')
      .select('file_path')
      .eq('id', photoId)
      .single();

    if (fetchError || !photo) {
      console.error('Error fetching measurement photo for deletion:', fetchError);
      return false;
    }

    // Delete from storage
    const { error: storageError } = await supabase.storage
      .from('user-uploads')
      .remove([photo.file_path]);

    if (storageError) {
      console.error('Error deleting from storage:', storageError);
      // Continue with database deletion even if storage deletion fails
    }

    // Delete from database
    const { error: dbError } = await supabase
      .from('measurement_photos')
      .delete()
      .eq('id', photoId);

    if (dbError) {
      console.error('Error deleting from database:', dbError);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Unexpected error deleting measurement photo:', error);
    return false;
  }
}

/**
 * Get public URL for a measurement photo
 */
export function getMeasurementPhotoUrl(filePath: string): string {
  const { data } = supabase.storage
    .from('user-uploads')
    .getPublicUrl(filePath);
  
  return data.publicUrl;
}









